import { Box, Button, Flex, Heading, Icon, Tag, Text, Tooltip, useToast } from "@chakra-ui/react";
import { debounce } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { FaCopy, FaEdit, FaEye, FaPlus, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BseStateNameDto,
  EventKindDto,
  EventListItemDto,
  ProjectDto,
  PublicEventStatusDto,
  SourcingEventSortField,
  SortDirection,
  useDeleteSourcingEventMutation,
  useListCombinedEventsForBuyerQuery,
  useUpdateBasicSourcingEventMutation,
  useUpdateSourcingEventMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { EntityTable } from "../../../common/entity-table/EntityTable";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { SelectorValue } from "../../../common/input/Selector/SelectorValue";
import { SingleSelector } from "../../../common/input/Selector/SingleSelector";
import { BUCKET_META, BucketKey, isBucketKey, statusesForBuckets } from "../../../common/layout/v2/statusBuckets";
import { useIsV2 } from "../../../common/layout/v2/useIsV2";
import { useRunsPublicProcurements } from "../../../common/organization/useRunsPublicProcurements";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { urls } from "../../../urls";
import { displayPersonName } from "../../contracts/view-single/sharing/AddExternalParticipantModal";
import { CreateSourcingEventModal } from "../../sourcing-events/buyer/CreateSourcingEventModal";
import { listItemIsAwarded, listItemIsDraft } from "../../sourcing-events/combinedEventStatuses";
import { useCombinedEventRowActions } from "../../sourcing-events/useCombinedEventRowActions";
import { CombinedEventStatusTag } from "../../v2/sourcing-event/CombinedEventStatusTag";

// Drafts get their project at creation (CreateSourcingEventModal takes projectId), so only live
// events are offered; PUBLISHED is the last V2 status the BFF's requireEditable() allows editing in.
const LINKABLE_EVENT_STATUSES = [
  PublicEventStatusDto.Published,
  BseStateNameDto.Published,
  BseStateNameDto.Completed,
];

export const SourcingEvents = ({ project }: { project: ProjectDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const authState = useLoggedInWithOrgContextState();
  const runsPublicProcurements = useRunsPublicProcurements();
  const navigate = useNavigate();
  const toast = useToast();
  const apiErrorDisplayer = useApiError();
  const confirmationModal = useConfirmationModal();
  const isV2 = useIsV2();
  const goToEventEdit = (event: EventListItemDto) =>
    event.kind === EventKindDto.V2
      ? navigate(urls.v2.sourcingEvents.view.go(event.id))
      : navigate(isV2 ? urls.v2.sourcingEvents.bseEdit.go(event.id) : urls.events.edit.go(event.id).intro);
  const goToEventView = (event: EventListItemDto) =>
    event.kind === EventKindDto.V2
      ? navigate(urls.v2.sourcingEvents.view.go(event.id))
      : navigate(isV2 ? urls.v2.sourcingEvents.bseView.go(event.id) : urls.events.view.go(event.id));

  const [filters, setFilters] = useState<{
    name: string;
    statuses: BucketKey[];
    sortBy: SourcingEventSortField | undefined;
    sortDirection: SortDirection | undefined;
    skip: number;
    limit: number;
  }>({
    name: "",
    statuses: [] as BucketKey[],
    sortBy: undefined,
    sortDirection: undefined,
    skip: 0,
    limit: 10,
  });

  const queryParams = {
    orgId: authState.selectedOrg.id,
    projectIds: [project.id],
    name: filters.name || undefined,
    statuses: filters.statuses.length ? statusesForBuckets(filters.statuses) : undefined,
    limit: filters.limit,
    skip: filters.skip,
    ...(filters.sortBy && { sortBy: filters.sortBy }),
    ...(filters.sortDirection && { sortDirection: filters.sortDirection }),
  };

  const {
    data: projectEvents,
    error: projectEventsError,
    isLoading: isLoadingProjectEvents,
    isFetching: isFetchingProjectEvents,
  } = useListCombinedEventsForBuyerQuery(queryParams);

  const [updateV1Event, { isLoading: isUpdatingV1Event }] = useUpdateBasicSourcingEventMutation();
  const [updateV2Event, { isLoading: isUpdatingV2Event }] = useUpdateSourcingEventMutation();
  const [deleteSourcingEvent] = useDeleteSourcingEventMutation();
  const { copyEvent, copyDisabledReason } = useCombinedEventRowActions();

  const [showModal, setShowModal] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [debouncedSearchName, setDebouncedSearchName] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<EventListItemDto>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearchName = useCallback(
    debounce((name: string) => setDebouncedSearchName(name), 300),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedTableSearch = useCallback(
    debounce(
      (searchString) =>
        setFilters((prev) => ({
          ...prev,
          name: searchString,
          skip: 0,
        })),
      300
    ),
    []
  );

  const { data: searchedEvents, isFetching: isLoadingSearchedEvents } = useListCombinedEventsForBuyerQuery({
    orgId: authState.selectedOrg.id,
    statuses: LINKABLE_EVENT_STATUSES,
    name: debouncedSearchName ? debouncedSearchName : undefined,
    limit: 10,
  });

  const onSelectedEvent = async (event: EventListItemDto) => {
    setSelectedEvent(event);
    const updatedProjects = Array.from(new Set([...(event.projects?.map((p) => p.id) ?? []), project.id]));
    try {
      if (event.kind === EventKindDto.V2) {
        await updateV2Event({
          eventId: event.id,
          sourcingEventV2PatchDto: { eventData: { projects: updatedProjects } },
        }).unwrap();
      } else {
        await updateV1Event({
          eventId: event.id,
          editSourcingEventRequest: { editProjects: updatedProjects },
        }).unwrap();
      }
    } catch (e) {
      apiErrorDisplayer.triggerSoft(e);
    }
    setSearchName("");
    setSelectedEvent(undefined);
  };

  const statusOptions: SelectorValue[] = (Object.keys(BUCKET_META) as BucketKey[]).map((bucket) => ({
    value: bucket,
    label: t(BUCKET_META[bucket].labelKey),
  }));

  const hasActiveFilters = useMemo(() => {
    return filters.name !== "" || filters.statuses.length > 0 || filters.sortBy !== undefined || filters.skip > 0;
  }, [filters]);

  const defaultColumns = useMemo(() => {
    const base = ["title", "status", "projects", "createdBy", "deadline", "winners", "actions"];
    return new Set(runsPublicProcurements ? base : ["title", "internalReference", ...base.slice(1)]);
  }, [runsPublicProcurements]);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => new Set(defaultColumns));
  const computeHidden = (key: string): boolean => !visibleColumns.has(key);
  const onShowColumn = useCallback((key: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);
  const onHideColumn = useCallback((key: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);
  const hasColumnChanges = useMemo((): boolean => {
    if (visibleColumns.size !== defaultColumns.size) return true;
    for (const k of visibleColumns) if (!defaultColumns.has(k)) return true;
    for (const k of defaultColumns) if (!visibleColumns.has(k)) return true;
    return false;
  }, [visibleColumns, defaultColumns]);

  const handleReset = useCallback(() => {
    setFilters({
      name: "",
      statuses: [],
      sortBy: undefined,
      sortDirection: undefined,
      skip: 0,
      limit: 10,
    });
    setVisibleColumns(new Set(defaultColumns));
  }, [defaultColumns]);

  const handleSort = useCallback(
    (column: SourcingEventSortField) => (direction: SortDirection) => {
      setFilters((prev) => ({
        ...prev,
        sortBy: column,
        sortDirection: direction,
        skip: 0,
      }));
    },
    []
  );

  if (projectEventsError) return apiErrorDisplayer.show(projectEventsError);

  return (
    <>
      <CreateSourcingEventModal
        showModal={showModal}
        setShowModal={setShowModal}
        projectId={project.id}
        goToEventOnCreation={false}
      />
      {confirmationModal.modal}
      <Box>
        {authState.selectedOrg.canCreateEvent && (
          <Flex columnGap={6} pb="5">
            <Box>
              <Button
                leftIcon={<Icon as={FaPlus} />}
                variant="outline"
                colorScheme="teal"
                size="sm"
                onClick={() => setShowModal(true)}
              >
                {t("New event")}
              </Button>
            </Box>
            <SingleSelector
              size="sm"
              isClearable
              placeholder={{ text: `${t("Connect sourcing event to this project")}...`, color: "" }}
              isLoading={isLoadingSearchedEvents || isUpdatingV1Event || isUpdatingV2Event}
              value={selectedEvent ? { label: selectedEvent.title, value: selectedEvent.id } : undefined}
              options={searchedEvents?.events.map((event) => ({ value: event.id, label: event.title })) ?? []}
              inputValue={searchName}
              onInputChange={(name: string) => {
                setSearchName(name);
                handleDebouncedSearchName(name);
              }}
              onChange={async (o) => {
                if (o === null) return;
                const event = searchedEvents?.events.find((e) => e.id === o?.value);
                if (!event) throw Error("Event not found");
                await onSelectedEvent(event);
              }}
            />
          </Flex>
        )}
        <Heading fontSize="lg" pb="5">
          {t("Sourcing events connected to this project")}
        </Heading>
        <EntityTable
          size="sm"
          onShowColumn={onShowColumn}
          onHideColumn={onHideColumn}
          hasColumnChanges={hasColumnChanges}
          columns={[
            {
              key: "title",
              label: t("Title"),
              onSearch: handleDebouncedTableSearch,
              onSort: handleSort(SourcingEventSortField.Title),
              currentSortDirection: filters.sortBy === SourcingEventSortField.Title ? filters.sortDirection : undefined,
              hidden: computeHidden("title"),
            },
            {
              key: "status",
              label: t("Status"),
              hidden: computeHidden("status"),
              multiSelectConfig: {
                options: statusOptions,
                onSelect: (values) => {
                  setFilters((prev) => ({
                    ...prev,
                    statuses: values.map((v) => v.value).filter(isBucketKey),
                    skip: 0,
                  }));
                },
                selectedValues: filters.statuses
                  ? filters.statuses.map((v) => ({
                      value: v,
                      label: statusOptions.find((s) => s.value === v)?.label || "",
                    }))
                  : [],
              },
            },
            {
              key: "projects",
              label: t("Projects"),
              hidden: computeHidden("projects"),
            },
            {
              key: "createdBy",
              label: t("Created by"),
              hidden: computeHidden("createdBy"),
            },
            {
              key: "internalReference",
              label: t("Internal reference"),
              onSort: handleSort(SourcingEventSortField.ReferenceNumber),
              currentSortDirection:
                filters.sortBy === SourcingEventSortField.ReferenceNumber ? filters.sortDirection : undefined,
              hidden: computeHidden("internalReference"),
            },
            {
              key: "deadline",
              label: t("Deadline"),
              onSort: handleSort(SourcingEventSortField.Deadline),
              currentSortDirection:
                filters.sortBy === SourcingEventSortField.Deadline ? filters.sortDirection : undefined,
              hidden: computeHidden("deadline"),
            },
            {
              key: "createdAt",
              label: t("Created at"),
              onSort: handleSort(SourcingEventSortField.CreatedAt),
              currentSortDirection:
                filters.sortBy === SourcingEventSortField.CreatedAt ? filters.sortDirection : undefined,
              hidden: computeHidden("createdAt"),
            },
            {
              key: "modifiedAt",
              label: t("Modified at"),
              onSort: handleSort(SourcingEventSortField.ModifiedAt),
              currentSortDirection:
                filters.sortBy === SourcingEventSortField.ModifiedAt ? filters.sortDirection : undefined,
              hidden: computeHidden("modifiedAt"),
            },
            {
              key: "winners",
              label: t("Winners"),
              hidden: computeHidden("winners"),
            },
            {
              key: "actions",
              label: t("Actions"),
              hidden: computeHidden("actions"),
            },
          ]}
          rows={
            projectEvents?.events?.map((event) => [
              <Button
                key="title"
                variant="unstyled"
                textAlign="left"
                minWidth="60"
                maxWidth="80"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                onClick={() => (listItemIsDraft(event) ? goToEventEdit(event) : goToEventView(event))}
                isDisabled={listItemIsDraft(event) ? !event.canEdit : !event.canView}
              >
                {event.title || t("Untitled")}
              </Button>,
              <Flex key="status" justifyContent="center">
                <CombinedEventStatusTag event={event} />
              </Flex>,
              <Flex key="projects" flexDirection="column" maxWidth="full">
                {event.projects?.map((p) => (
                  <Tag key={p.id} size="sm" width="full" maxWidth="40" mb="1">
                    <Tooltip label={p.name} rounded="md" size="sm">
                      <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" width="full">
                        {p.name}
                      </Text>
                    </Tooltip>
                  </Tag>
                ))}
              </Flex>,
              displayPersonName(event.createdBy),
              event.referenceId,
              event.deadline ? displayDate(event.deadline) : "",
              event.createdAt ? displayDate(event.createdAt) : "",
              event.modifiedAt ? displayDate(event.modifiedAt) : "",
              listItemIsAwarded(event) ? (event.winners?.join(", ") ?? "None") : "",
              <Flex key="actions" flexDirection="row" columnGap={1} align="center">
                <Explanation enabled={!event.canEdit} text={copyDisabledReason(event) ?? ""}>
                  <Button
                    isDisabled={!event.canEdit}
                    leftIcon={<Icon as={FaCopy} />}
                    variant="outline"
                    colorScheme="teal"
                    size="xs"
                    onClick={() => copyEvent(event)}
                  >
                    {t("Copy")}
                  </Button>
                </Explanation>
                {listItemIsDraft(event) ? (
                  <>
                    <Explanation text={t("Only admins and creators can edit events")} enabled={!event.canEdit}>
                      <Button
                        leftIcon={<Icon as={FaEdit} w="15px" h="15px" />}
                        variant="outline"
                        colorScheme="teal"
                        isDisabled={!event.canEdit}
                        size="xs"
                        onClick={() => goToEventEdit(event)}
                      >
                        {t("Edit")}
                      </Button>
                    </Explanation>
                    <Explanation text={t("Only admins and creators can delete events")} enabled={!event.canEdit}>
                      <Button
                        leftIcon={<Icon as={FaTimes} w="15px" h="15px" />}
                        variant="outline"
                        isDisabled={!event.canEdit}
                        colorScheme="red"
                        size="xs"
                        onClick={async () => {
                          confirmationModal.show({
                            title: t("Delete draft?"),
                            description: t("Are you sure you want to delete this draft?"),
                            onConfirmed: async () => {
                              const result = await deleteSourcingEvent({
                                eventId: event.id,
                              });

                              if ("data" in result) {
                                toast({
                                  title: t("Successfully deleted!"),
                                  description: t("The draft was successfully deleted"),
                                  status: "success",
                                });
                              } else {
                                apiErrorDisplayer.trigger(result.error);
                              }
                            },
                          });
                        }}
                      >
                        {t("Delete")}
                      </Button>
                    </Explanation>
                  </>
                ) : (
                  <Explanation
                    enabled={!event.canView}
                    text={t("Contact your admin to get access to view this event") ?? ""}
                  >
                    <Button
                      leftIcon={<Icon as={FaEye} w="15px" h="15px" />}
                      isDisabled={!event.canView}
                      variant="solid"
                      colorScheme="teal"
                      size="xs"
                      onClick={() => goToEventView(event)}
                    >
                      {t("View event")}
                    </Button>
                  </Explanation>
                )}
              </Flex>,
            ]) ?? []
          }
          isLoading={isLoadingProjectEvents || isFetchingProjectEvents}
          count={projectEvents?.count ?? 0}
          limit={filters.limit}
          skip={filters.skip}
          setSkip={(skip) => setFilters((prev) => ({ ...prev, skip }))}
          onReset={handleReset}
          hasActiveFilters={hasActiveFilters}
        />
      </Box>
    </>
  );
};
